/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { BlogCardFragment, BlogCategoryFragment } from "@cosmos/hygraph-schema";
import { useMessages } from "@epic-mw/i18n";

import {
	ContentLargeImageSection,
	ResponsiveContainer,
	SectionContainer,
	BlogCards,
	CosmosTagProps,
	Wrap,
	Animate,
} from "@components/elements";
import { useCosmosComponentContext } from "@components/context";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

import { FeedItem } from "./feed-card.types";
import { getBlogCardFragment, getContentLargeImageFragment } from "./feed-card.utils";

export interface FeedCardOrganismProps {
	locale: string;
	locales: string[];
	items?: FeedItem[];
	blogCategories?: BlogCategoryFragment[];
	featuredLabel?: string | null;
	enableAnimate?: boolean;
	flipFeatured?: boolean;
	disableFeatured?: boolean;
	disablePaddingBlockEnd?: boolean;
	currentBrand?: string;
}

const cardRowStyles = css({
	"&.blog-cards": {
		w: "full",
	},

	"&.blog-cards .cosmos-text.heading": {
		textStyle: "headingLG",
	},
});

export const FeedCardOrganism = ({
	locale,
	locales,
	items = [],
	blogCategories = [],
	featuredLabel,
	enableAnimate = true,
	flipFeatured,
	disableFeatured,
	disablePaddingBlockEnd,
	currentBrand,
}: FeedCardOrganismProps): React.JSX.Element | null => {
	const pinnedBadge: CosmosTagProps = {
		label: featuredLabel || "Featured",
		icon: "Star",
	};
	const { feedRootPath = "/feed" } = useCosmosComponentContext();
	const messages = useMessages();
	const readMoreLabel = messages["epic.cosmos.read.more"];

	if (!items || !items.length) return null;

	//When less than 4 cards we simple render them as regular blog cards
	const lessThan4 = items.length < 4;

	let index = 0;
	const rows = [];

	// by default, the first row contains a prominent blog card
	const hasFeaturedRow = !disableFeatured && !lessThan4;
	if (hasFeaturedRow) {
		const featuredCard = items[index];
		rows.push(
			<div
				className={cx(
					"feed-card-content-large-image-section",
					css({
						"&& .section-container": {
							paddingBlockStart: "eds.dimension.96",
							paddingBlockEnd: "eds.dimension.96",
							smDown: { paddingBlockStart: "eds.dimension.48", paddingBlockEnd: "eds.dimension.48" },
							"& .content-large-image-section > div": {
								gap: "eds.dimension.64",
								lgDown: {
									gap: "eds.dimension.48",
									"& .content-large-image-main-stack-image": {
										order: "1",
										mt: "eds.dimension.48",
										mb: "0",
									},
								},
								"& .content-large-image-main-stack": {
									alignContent: "center",
									"& > div": {
										gap: "eds.dimension.16",
									},
								},
								"& .cosmos-date": {
									textStyle: "headingSM",
									color: "eds.text.primary",
								},
								"& .title": {
									marginBottom: "eds.dimension.16",
								},
								"& .desc-buttons": {
									gap: "eds.dimension.24",
								},
							},
						},
					}),
				)}
			>
				<ContentLargeImageSection
					key={`row-${index}`}
					{...getContentLargeImageFragment(
						locale,
						locales,
						featuredCard,
						blogCategories,
						pinnedBadge,
						readMoreLabel,
						feedRootPath,
						currentBrand,
					)}
					flip={!flipFeatured}
					sectionIndex={0}
					enableAnimate={enableAnimate}
				/>
			</div>,
		);
		index += 1;
	}

	// the remaining rows contain three blog cards each
	const remainingCards: BlogCardFragment[] = items.slice(index).map((item) => {
		return getBlogCardFragment(locale, locales, item, blogCategories, pinnedBadge, feedRootPath, currentBrand);
	});
	if (remainingCards.length) {
		rows.push(
			<Wrap
				key={`row-${index}-animate-wrap`}
				condition={enableAnimate}
				wrap={(children) => (
					<Animate delay={0.75} type="slide" customCss={css.raw({ flexBasis: "50%" })}>
						{children}
					</Animate>
				)}
			>
				<BlogCards key={`row-${index}`} className={cardRowStyles} cards={remainingCards} />
			</Wrap>,
		);
	}

	return (
		<div className={cx("feed-card-organism", css({ w: "full" }))}>
			{hasFeaturedRow ? rows[0] : null}

			<ResponsiveContainer>
				<SectionContainer disablePaddingBlockEnd={disablePaddingBlockEnd} disablePaddingBlockStart={hasFeaturedRow}>
					<VStack alignItems="flex-start" gap="eds.dimension.16">
						{hasFeaturedRow ? rows.slice(1) : rows}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</div>
	);
};
